import { logger, Metrics } from 'src/utils';
import { Logs, MethodName } from 'src/constants/metrics';

type ForumsRequestInit = {
  isNonJsonRequest?: boolean;
} & RequestInit;
const CSRF_TOKEN = 'anti-csrftoken-a2z';
const CONTENT_TYPE = 'content-type';
const APPLICATION_JSON_CONTENT_TYPE = 'application/json';
export const USER_TOKEN = 'x-amzn-forums-user-token';
/**
 * This method is intended to be reused for client side navigation to
 * pages that require users to be authenticated
 */
export const redirectToLoginPage = () => {
  const { pathname, search, origin } = window.location;
  const currentPath = encodeURIComponent(`${pathname}${search}`);
  const redirectURL = `${origin}/gp/sign-in/sign-in.html?maxAuthAge=18000&destination=${currentPath}`;
  window.location.assign(redirectURL);
};

const getCustomHeaders = (includeCsrfToken: boolean) => {
  const csrfToken = document.querySelector(`meta[name=${CSRF_TOKEN}]`)?.getAttribute('content');
  const userToken = document.querySelector(`meta[name=${USER_TOKEN}]`)?.getAttribute('content');
  return {
    ...(includeCsrfToken && csrfToken ? {
      [CSRF_TOKEN]: csrfToken,
    } : {}),
    ...(userToken ? {
      [USER_TOKEN]: userToken,
    } : {}),
  };
};

export const customFetch = async (
  url: RequestInfo,
  options?: ForumsRequestInit,
  onError: (
    error: Error,
    responseInfo?: {
      status: Number,
      data: { [key: string]: any }
    }) => any = (error: Error) => { throw error; },
  retries = 0,
  includeCsrfToken = true,
  expectedContentType: null | string = APPLICATION_JSON_CONTENT_TYPE,
  onSuccess?: (data: any, responseHeaders: Headers) => { data: any, responseHeaders: Headers },
): Promise<any> => {
  try {
    const customHeaders = getCustomHeaders(includeCsrfToken);
    const isNonJsonRequest = !!options?.isNonJsonRequest;
    const requestOptions = {
      ...options,
      headers: {
        ...(!isNonJsonRequest ? { [CONTENT_TYPE]: APPLICATION_JSON_CONTENT_TYPE } : {}),
        ...customHeaders,
        ...options?.headers,
      },
    };
    const response = await fetch(url, requestOptions);
    const requestMetric = new Metrics(MethodName.apiRequest);
    let data;
    const responseContentType = response.headers.get('content-type');
    const isJsonResponse = responseContentType && responseContentType.includes('application/json');
    if (isJsonResponse) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    if (!response.ok) {
      const error = new Error(`Request failed with status: ${response.status}`);
      const logContext = {
        url,
        options,
        responseData: typeof data === 'object' ? data : undefined,
        responseContentType,
        responseHeaders: Object.fromEntries(response.headers),
      };
      requestMetric.publishHttpRequestMetric(url as string, response, false);
      /**
       * @todo implement exponential backoff if required after testing
       */
      if (response.status === 504 && retries > 0) {
        logger.warn(Logs.apiTimeoutFailure, error, logContext);
        return await customFetch(url, options, onError, retries - 1, true, null, onSuccess);
      } if (response.status === 401) {
        redirectToLoginPage();
      } else {
        if (response.status === 498) {
          logger.warn(Logs.apiUserTokenFailure, error, logContext);
          const { pathname, search, origin } = window.location;
          const currentPath = encodeURI(`${pathname}${search}`);
          window.location.assign(`${origin}${currentPath}`);
        } else if (response.status === 400) {
          logger.warn(Logs.apiBadRequestFailure, error, logContext);
        } else if (response.status === 403) {
          if (isJsonResponse) {
            logger.warn(Logs.apiServiceAuthFailure, error, logContext);
          } else {
            logger.warn(Logs.apiAuthFailure, error, logContext);
            redirectToLoginPage();
          }
        } else {
          logger.error(Logs.apiRequestFailure, error, logContext);
        }
        return onError(error, { status: response.status, data });
      }
    } else {
      if (!!expectedContentType
        && responseContentType
        && !responseContentType.includes(expectedContentType)
      ) {
        throw new Error(`ContentType mismatch. Expected: ${expectedContentType} but received: ${responseContentType}`);
      }
      requestMetric.publishHttpRequestMetric(url as string, response, true);
    }
    if (onSuccess) {
      return onSuccess(data, response.headers);
    }

    return data;
  } catch (error: any) {
    if (error.name !== 'AbortError') {
      logger.error(Logs.apiRequestException, error, { url, options });
    }

    return onError(error);
  }
};

export const constructQueryParams = (
  queryParams: Record<string, string | number | undefined>,
  endpoint: string,
) => {
  // URLSearchParams does not allow undefined in the object's value
  const requestQueryParams: Record<string, string> = Object.keys(queryParams).reduce((acc, key) => {
    if (queryParams[key]) {
      return { ...acc, [key]: queryParams[key] };
    }
    return acc;
  }, {});
  const queryString = new URLSearchParams(requestQueryParams).toString();
  return `${endpoint}?${queryString}`;
};
